import {signOut} from 'firebase/auth';

import {auth} from '-/firebase';
import {wipeUserCaches} from '-/data/bootstrapCache';

// Session timeout (#1048/#1049). The real problem is shared apparatus tablets:
// an admin signs in, forgets to sign out, and the next person is still acting as
// them. So the primary control is an INACTIVITY timeout — the device sitting
// untouched (e.g. overnight) signs the previous user out — with an absolute cap
// as a backstop.
//
//   - "Remember me" OFF (default / shared tablet): signed out after 1 hour of
//     no interaction, and after 12 hours no matter what.
//   - "Remember me" ON (personal phone): stays signed in for 30 days with no
//     idle logout.
//
// These windows are the knobs the product asked us to leave adjustable.
export const IDLE_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour of inactivity
export const SESSION_TIMEOUT_MS = 12 * 60 * 60 * 1000; // 12 hour absolute backstop
export const REMEMBER_ME_TIMEOUT_MS = 30 * 24 * 60 * 60 * 1000; // 30 days (personal device)

const REMEMBER_KEY = 'sessionRemember';
const EXPIRES_AT_KEY = 'sessionExpiresAt'; // absolute cutoff
const ACTIVITY_AT_KEY = 'sessionActivityAt'; // last interaction (drives idle timeout)
const EXPIRED_MESSAGE_KEY = 'sessionExpired'; // why the last session ended

// Storage can be missing, blocked, or full (EAGLEVALLEYRESCUE-7). Intentionally
// handle those failures here so auth cleanup and the login screen still run.
// Mirror values in memory to keep timeouts working for the open page; persistence
// across reloads still depends on the browser allowing localStorage.
const memoryValues = new Map();
const memoryOnlyKeys = new Set();

function readSessionValue(key) {
    // A failed write/removal must not be replaced by an older persisted value.
    if (!memoryOnlyKeys.has(key)) {
        try {
            const value = localStorage.getItem(key);
            memoryValues.set(key, value);
            return value;
        } catch {
            // Fall back to the last value read or written in this page.
        }
    }
    return memoryValues.get(key) ?? null;
}

function writeSessionValue(key, value) {
    memoryValues.set(key, value);
    memoryOnlyKeys.add(key);
    try {
        if (value === null) {
            localStorage.removeItem(key);
        } else {
            localStorage.setItem(key, value);
        }
        memoryOnlyKeys.delete(key);
    } catch {
        // Keep the in-memory value authoritative until a write succeeds.
    }
}

// Begin tracking a brand-new sign-in. Records the absolute cutoff and seeds the
// activity clock. Stored in localStorage so the timeout survives reload, browser
// restart and tablet sleep.
export function startSession(remember) {
    const now = Date.now();
    writeSessionValue(REMEMBER_KEY, remember ? '1' : '0');
    writeSessionValue(EXPIRES_AT_KEY, String(now + (remember ? REMEMBER_ME_TIMEOUT_MS : SESSION_TIMEOUT_MS)));
    writeSessionValue(ACTIVITY_AT_KEY, String(now));
    writeSessionValue(EXPIRED_MESSAGE_KEY, null);
}

// Push the inactivity clock forward. Called (throttled) on user interaction.
export function recordActivity() {
    writeSessionValue(ACTIVITY_AT_KEY, String(Date.now()));
}

// Evaluate the current session. Returns null when nothing is tracked (e.g. a
// station login or a session created before this shipped), otherwise:
//   { remaining, reason }
// where `remaining` is ms until the session must end (0 = end it now) and
// `reason` is 'idle' or 'absolute'.
export function evaluateSession() {
    const expiresAtRaw = readSessionValue(EXPIRES_AT_KEY);
    if (!expiresAtRaw) {
        return null;
    }

    const now = Date.now();
    let remaining = parseInt(expiresAtRaw, 10) - now;
    let reason = 'absolute';

    // The idle timeout only applies to non-remembered (shared-device) sessions.
    if (readSessionValue(REMEMBER_KEY) !== '1') {
        const activityAt = parseInt(readSessionValue(ACTIVITY_AT_KEY) || '0', 10);
        const idleRemaining = activityAt + IDLE_TIMEOUT_MS - now;
        if (idleRemaining < remaining) {
            remaining = idleRemaining;
            reason = 'idle';
        }
    }

    return {remaining: Math.max(0, remaining), reason};
}

// Forget all session tracking. Used on a normal sign-out so stale timestamps
// don't carry over to the next account on this (possibly shared) device.
export function clearSession() {
    writeSessionValue(REMEMBER_KEY, null);
    writeSessionValue(EXPIRES_AT_KEY, null);
    writeSessionValue(ACTIVITY_AT_KEY, null);
    // Drop the per-user first-paint cache so a previous user's PII doesn't linger
    // on a shared device. Covers normal sign-out, session expiry (expireSession
    // calls this) and the archived-user auto-signout that routes through here.
    wipeUserCaches();
}

// Force a sign-out because the session timed out. Leaves a message behind so the
// login screen can explain why the user was bounced.
export async function expireSession(message) {
    writeSessionValue(EXPIRED_MESSAGE_KEY, message);
    clearSession();
    await signOut(auth);
}

// Read-and-clear the "why were you signed out" message. Returns the string once
// after a timeout, or null.
export function consumeSessionExpiredMessage() {
    const message = readSessionValue(EXPIRED_MESSAGE_KEY);
    if (message) {
        writeSessionValue(EXPIRED_MESSAGE_KEY, null);
    }
    return message;
}
